import qs from 'qs';

import { BrandStoryLanding } from 'brand/components/BrandStoryLanding';
import { BREADCRUMBS } from 'breadcrumbs/constants/breadcrumbs';
import { bugsnag } from 'shared/utils/services/bugsnag';
import { cmsRequest } from 'server/services/cms-service/cmsRequest';
import { getBreadcrumbs } from 'server/utils/breadcrumbs/getBreadcrumbs';
import { FeatureFlags } from 'featureFlags/constants';
import { fetchGlobalPricingId } from 'server/utils/fetchGlobalPricingId';
import { getThemeBackgroundColorFromSpaceArticle } from 'shared/utils/getThemeBackgroundColorFromSpaceArticle';
import { ICMSBreadcrumb } from 'shared/types/Breadcrumbs';
import { IConstructorBrowseResponse } from 'shared/types/ConstructorBrowseResponse';
import { ISpaceArticleSectionFeed } from 'shared/types/SpaceArticleSection';
import { PlpPageComponent as PLPLandingPageComponent } from 'product-landing-pages/components/PlpPageComponent';
import { useFeatureFlag } from 'shared/hooks/useFeatureFlag';
import { getBrowseResults } from 'server/utils/constructor/getBrowseResults';
import { isSearchCrawler } from 'shared/utils/isSearchCrawler';
import { getRedirectionPath } from 'localization/utils/getRedirectionPath';
import { setServerSideTranslations } from 'localization/utils/setServerSideTranslations';
import { getCollectionsGrpc } from 'server/grpc/collections/collections.grpc';
import { requestService } from 'shared/utils/services/requestService';
import { useEffect, useState } from 'react';
import { useRouter } from 'next/router';
import { getConsumerSearchServerSideData } from 'server/utils/getConsumerSearchDataServerSide';

function replaceUndefinedWithNull(obj) {
  if (!obj) return null;
  if (Array.isArray(obj)) {
    // eslint-disable-next-line unicorn/no-array-callback-reference
    return obj.map(replaceUndefinedWithNull);
  }
  if (obj !== null && typeof obj === 'object') {
    return Object.fromEntries(
      Object.entries(obj).map(([key, value]) => [key, replaceUndefinedWithNull(value)]),
    );
  }
  return obj === undefined ? null : obj;
}
const MANIFESTO = 'manifesto';

interface IManifestoProps {
  article: ISpaceArticleSectionFeed;
  backgroundColor?: string | null;
  breadcrumbs: ICMSBreadcrumb;
  browseKey: string;
  browseValue: string;
  constructorResponse: IConstructorBrowseResponse;
  globalPricingId: string;
  hasProducts?;
  isInApp?: boolean;
  boolean;
  showCollection: string;
  slug: string;
  isCrawler?: boolean;
  consumerSearchCollection?: any;
  consumerSearchInitialData?: string;
}

const Manifesto = ({
  article,
  backgroundColor = null,
  breadcrumbs,
  browseKey,
  browseValue,
  constructorResponse,
  globalPricingId,
  hasProducts = true,
  isInApp = false,
  showCollection,
  consumerSearchCollection,
  isCrawler = false,
  consumerSearchInitialData = '{}',
}: IManifestoProps) => {
  const { response } = constructorResponse || {};
  const { collection } = response || {};

  const [consumerCollection, setConsumerCollection] = useState<any>(consumerSearchCollection);
  const { display_name: displayName, data } = collection || {};

  const router = useRouter();

  const isNewSearchPageEnabled = useFeatureFlag(FeatureFlags.TEMP_WEB_ENABLE_SEARCH_MICROFRONTEND);

  const [consumerAtricles, setConsumerArticles] = useState<any>({
    article,
    backgroundColor,
  });

  useEffect(() => {
    const getCSArticle = async () => {
      try {
        const res2 = await requestService({
          url: `/web-api/consumer-search/get-collection-details?slug=${MANIFESTO}`,
          method: 'get',
        });

        setConsumerCollection(res2?.data?.collection || {});

        // console.log(res2);

        if (res2?.data?.collection?.publishedCmsArticleSlug) {
          const { publishedCmsArticleSlug } = res2?.data?.collection || {};

          try {
            const data2 = await requestService({
              url: `/web-api/v1/cms/cms_articles/${publishedCmsArticleSlug}`,
              method: 'get',
            });
            if (data2) {
              backgroundColor =
                data2?.sections?.length > 0
                  ? getThemeBackgroundColorFromSpaceArticle(data2?.sections?.[0])
                  : null;
              setConsumerArticles({
                article: data2 as any,
                backgroundColor,
              });
            }
          } catch (error) {
            bugsnag(error, { context: 'Failed to fetch CMS article for Manifesto' });
            setConsumerArticles({
              article: null,
              backgroundColor: null,
            });
          }
        }
      } catch (error) {
        bugsnag(error, { context: 'Failed to fetch CMS Space for Manifesto' });
        setConsumerCollection({});
        setConsumerArticles({
          article: null,
          backgroundColor: null,
        });
      }
    };

    if (isNewSearchPageEnabled) {
      void getCSArticle();
    }
  }, [isNewSearchPageEnabled]);

  const enableBrandStory =
    useFeatureFlag(FeatureFlags.TEMP_WEB_ENABLE_BRAND_STORY) && article?.sections?.length > 0;
  const enableNextPrev = useFeatureFlag(FeatureFlags.WEB_ENABLE_TOGGLE_NEXT_PREV_MANIFESTO);

  const {
    name = displayName,
    description = data?.description,
    metadataDescription = data?.metadataDescription,
    titleTag = data?.title || displayName,
  } = breadcrumbs || {};

  return enableBrandStory ? (
    <BrandStoryLanding
      article={isNewSearchPageEnabled ? consumerAtricles.article : article}
      backgroundColor={isNewSearchPageEnabled ? consumerAtricles.backgroundColor : backgroundColor}
      browseKey={browseKey}
      browseValue={browseValue}
      collectionDisplayName={consumerCollection?.title || displayName}
      constructorResponse={constructorResponse}
      description={consumerCollection?.description || description}
      displayName={MANIFESTO}
      enableButtons={enableNextPrev}
      globalPricingId={globalPricingId}
      isInApp={isInApp}
      metadataDescription={metadataDescription}
      metaTitle={`${consumerCollection?.title || titleTag || displayName} `}
      showCollection={(router?.query?.showCollection || showCollection) as any}
      hideCollection={!hasProducts}
      consumerSearchFilters={{ collectionSlug: MANIFESTO, theme: 'dark' }}
      consumerSearchInitialData={JSON.parse(consumerSearchInitialData)}
    />
  ) : (
    <PLPLandingPageComponent
      browseKey={browseKey}
      browseValue={browseValue}
      constructorResponse={constructorResponse}
      description={description}
      displayName={name}
      globalPricingId={globalPricingId}
      metadataDescription={metadataDescription}
      metaTitle={titleTag || `${displayName} `}
      isCrawler={isCrawler}
      consumerSearchFilters={{ collectionSlug: MANIFESTO, theme: 'dark' }}
      consumerSearchInitialData={JSON.parse(consumerSearchInitialData)}
    />
  );
};

export const getServerSideProps = async ({ query, resolvedUrl, req, res, locale }) => {
  const q = qs.parse(query);
  const { headers, featureFlags } = req;
  const { showCollection = 'false', app } = q;
  const globalPricingId = await fetchGlobalPricingId({ req, res });
  const breadcrumbs = await getBreadcrumbs({
    req,
    res,
    path: `${BREADCRUMBS.BRAND}/${MANIFESTO}`,
  });
  const isCrawler = isSearchCrawler(headers);
  const isConsumerSearchEnabled = featureFlags?.[FeatureFlags.TEMP_WEB_ENABLE_SEARCH_MICROFRONTEND];

  if (
    !featureFlags?.[FeatureFlags.TEMP_WEB_ENABLE_MANIFESTO] ||
    !featureFlags?.[FeatureFlags.TEMP_WEB_ENABLE_BRAND_STORY]
  ) {
    return {
      redirect: {
        permanent: false,
        destination: getRedirectionPath('/', locale),
      },
    };
  }

  if (isConsumerSearchEnabled) {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const consumerSearchInitialData = await getConsumerSearchServerSideData({
      filters: { ...q, pageFilters: { collectionSlug: MANIFESTO } },
      pageNumber: req.query?.pageNumber || 1,
    });
    let backgroundColor = '';
    let article = null;
    let details: any = null;
    const grpcClient = getCollectionsGrpc();
    try {
      const res2 = await grpcClient.getCollectionsDetails({
        slug: MANIFESTO,
        salesChannel: 1,
        id: null,
      });

      details = res2?.data?.collection;

      if (res2?.data?.collection?.publishedCmsArticleSlug) {
        const { publishedCmsArticleSlug } = res2?.data?.collection || {};

        try {
          const data = cmsRequest(req).get(`api/v1/cms/cms_articles/${publishedCmsArticleSlug}`);
          article = await data.json();

          backgroundColor =
            article?.sections?.length > 0
              ? getThemeBackgroundColorFromSpaceArticle(article?.sections?.[0])
              : null;
        } catch (error) {
          bugsnag(error, { context: 'Failed to fetch CMS article for Manifesto' });
        }
      }
    } catch (error) {
      bugsnag(error, { context: 'Failed to fetch CMS Space for Manifesto' });
    }

    return {
      props: {
        ...(await setServerSideTranslations({ locale })),
        article,
        backgroundColor,
        breadcrumbs,
        browseKey: 'collection_id',
        browseValue: MANIFESTO,
        constructorResponse: {},
        displayName: MANIFESTO,
        globalPricingId,
        showCollection,
        slug: MANIFESTO,
        isCrawler,
        isInApp: app === 'y',
        consumerSearchCollection: replaceUndefinedWithNull(details),
        consumerSearchInitialData: JSON.stringify({}),
      },
    };
  }

  try {
    // Check if collection for this slug exists, if not browse for brand
    const collectionResponse = await getBrowseResults({
      req,
      res,
      query,
      resolvedUrl,
      filterKey: 'collection_id',
      filterValue: MANIFESTO,
    });

    // eslint-disable-next-line camelcase
    const {
      response: {
        collection: {
          display_name: displayName,
          id,
          data: { published_cms_article_slug: publishedCmsArticleSlug },
        },
      },
    }: IConstructorBrowseResponse = collectionResponse;

    if (publishedCmsArticleSlug) {
      try {
        const data = cmsRequest(req).get(`api/v1/cms/cms_articles/${publishedCmsArticleSlug}`);
        const article: ISpaceArticleSectionFeed = await data.json();

        const backgroundColor: string =
          article?.sections?.length > 0
            ? getThemeBackgroundColorFromSpaceArticle(article.sections[0])
            : null;

        return {
          props: {
            ...(await setServerSideTranslations({ locale })),
            breadcrumbs,
            browseKey: 'collection_id',
            browseValue: id,
            constructorResponse: collectionResponse,
            displayName,
            globalPricingId,
            showCollection,
            slug: MANIFESTO,
            isCrawler,
            isInApp: app === 'y',
            article,
            backgroundColor,
          },
        };
      } catch (error) {
        bugsnag(error, { context: 'Failed to fetch CMS Space for Manifesto' });
        return {
          props: {
            ...(await setServerSideTranslations({ locale })),
            breadcrumbs,
            browseKey: 'collection_id',
            browseValue: id,
            constructorResponse: collectionResponse,
            displayName,
            globalPricingId,
            showCollection,
            slug: MANIFESTO,
            isCrawler,
            isInApp: app === 'y',
          },
        };
      }
    }

    return {
      props: {
        ...(await setServerSideTranslations({ locale })),
        breadcrumbs,
        browseKey: 'collection_id',
        browseValue: id,
        constructorResponse: collectionResponse,
        displayName,
        globalPricingId,
        showCollection,
        slug: MANIFESTO,
        isCrawler,
        isInApp: app === 'y',
      },
    };
  } catch {
    return {
      redirect: {
        permanent: false,
        destination: getRedirectionPath('/', locale),
      },
    };
  }
};

export default Manifesto;
